// src/components/Toolbox.jsx
import React, { useState, useEffect, useRef } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import BusinessInfo from "./BusinessInfo";
import WorkoutTemplate from "./WorkoutTemplate";
import Exercises from "./Exercises";
import Recipes from "./Recipes";
import ContentLibrary from "./ContentLibrary";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { useLocation } from "react-router-dom";
import appInsights from "../../AppInsights";

function Toolbox() {
  const { trans } = useCustomTranslation();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || "businessInfo"
  );

  // Refs for time tracking
  const pageLoadTime = useRef(Date.now());
  const lastTabChangeTime = useRef(Date.now());
  const timeTrackingInterval = useRef(null);
  const totalTimeMinutes = useRef(0);

  // Set up time tracking interval
  const setupTimeTrackingInterval = () => {
    timeTrackingInterval.current = setInterval(() => {
      totalTimeMinutes.current += 1; // Add 1 minute each interval
      trackTimeSpentEvent("Toolbox_Time_Update", totalTimeMinutes.current);
    }, 60000); // Every 60 seconds
  };

  // Helper to get current time spent in minutes
  const getCurrentTimeSpentMinutes = () => {
    const seconds = (Date.now() - pageLoadTime.current) / 1000;
    return Math.floor(seconds / 60);
  };

  // Generic time tracking function
  const trackTimeSpentEvent = (eventName, minutes) => {
    appInsights.trackEvent({
      name: eventName,
      properties: {
        totalTimeMinutes: minutes,
        totalTimeFormatted: `${minutes} minutes`,
        currentActiveTab: activeTab,
        pageLoadTimestamp: new Date(pageLoadTime.current).toISOString(),
        eventTimestamp: new Date().toISOString(),
      },
    });
  };

  // Track total time spent on toolbox page
  const trackToolboxPageTimeSpent = () => {
    const finalMinutes = getCurrentTimeSpentMinutes();
    
    appInsights.trackEvent({
      name: "Toolbox_Time_Spent",
      properties: {
        totalTimeMinutes: finalMinutes,
        totalTimeFormatted: `${finalMinutes} minutes`,
        lastActiveTab: activeTab,
        pageLoadTimestamp: new Date(pageLoadTime.current).toISOString(),
        pageExitTimestamp: new Date().toISOString(),
      },
    });
  };

  // 🔹 Track Toolbox page view
  useEffect(() => {
    const openTime = new Date().toLocaleString();
    appInsights.trackPageView({
      name: "Toolbox Page",
      properties: {
        openedAt: openTime,
        initialTab: activeTab,
      },
    });

    // Track immediate page load
    trackTimeSpentEvent("Toolbox_Page_Loaded", 0);

    // Set up time tracking interval
    setupTimeTrackingInterval();

    // Set up visibility change tracking
    const handleVisibilityChange = () => {
      if (document.hidden) {
        trackTimeSpentEvent("Toolbox_Page_Hidden", getCurrentTimeSpentMinutes());
      } else {
        trackTimeSpentEvent("Toolbox_Page_Visible", getCurrentTimeSpentMinutes());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Clean up interval and event listener
      if (timeTrackingInterval.current) {
        clearInterval(timeTrackingInterval.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 🔹 Handle tab change
  const handleSelect = (key) => {
    const now = Date.now();
    const timeSpentMinutes = Math.floor((now - lastTabChangeTime.current) / 60000);

    appInsights.trackEvent({
      name: "Toolbox_Tab_Clicked",
      properties: {
        previousTab: activeTab,
        newTab: key,
        timeSpentOnPreviousTab: `${timeSpentMinutes} minutes`,
        totalTimeOnPage: `${getCurrentTimeSpentMinutes()} minutes`,
      },
    });

    setActiveTab(key);
    lastTabChangeTime.current = now;
  };

  // 🔹 On page unload
  useEffect(() => {
    return () => {
      trackToolboxPageTimeSpent();
      
      // Also track page unload for backward compatibility
      appInsights.trackEvent({
        name: "Toolbox_Page_Unload",
        properties: {
          lastActiveTab: activeTab,
          totalTimeMinutes: getCurrentTimeSpentMinutes(),
          totalTimeFormatted: `${getCurrentTimeSpentMinutes()} minutes`,
        },
      });
    };
  }, [activeTab]);

  return (
    <div className="formContainer">
      <Tabs
        activeKey={activeTab}
        onSelect={handleSelect}
        id="controlled-tab-example"
        className="mb-3 fs-5"
      >
        <Tab eventKey="businessInfo" title={trans("Toolbox.businessInfo")}>
          {activeTab === "businessInfo" && <BusinessInfo />}
        </Tab>
        <Tab
          eventKey="workoutTemplate"
          title={trans("Toolbox.workoutTemplates")}
        >
          {activeTab === "workoutTemplate" && <WorkoutTemplate />}
        </Tab>
        <Tab eventKey="exercises" title={trans("Toolbox.exercises")}>
          {activeTab === "exercises" && <Exercises />}
        </Tab>
        <Tab eventKey="recipes" title={trans("Toolbox.recipes")}>
          {activeTab === "recipes" && <Recipes />}
        </Tab>
        <Tab eventKey="contentLibrary" title={trans("Toolbox.contentLibrary")}>
          {activeTab === "contentLibrary" && <ContentLibrary />}
        </Tab>
      </Tabs>
    </div>
  );
}

export default Toolbox;